import { KeyboardEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Pill,
  Stack,
  Typography,
} from "@financeable-com-au/financeable-ui";
import { ROUTES, SUCCESS_MESSAGE } from "@/constants";
import {
  tenantStateSelector,
  getTenantById,
  resetTenantForm,
  setTenantForm,
  setTenantShowErrors,
  updateTenant,
} from "@/store/slices/tenantSlice";
import { AppDispatch } from "@/store/store";
import { ITenantFormState, ITenantSlice, ITenantState } from "@/store/slices/types/tenantSliceTypes";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { IDispatchResponse } from "@/utils/interface";
// @ts-expect-error: ignoring as it is existing js file
import regex from "@/utils/regex";
import { Loader } from "@/components/Loader";
import SectionCard from "../Invoice/components/SectionCard";

const isValidEmail = (email: string) => regex.email.test(email);

const TenantDetail = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();

  const tenantState = useSelector<ITenantSlice, ITenantState>(tenantStateSelector);
  const { form, currentTenant, gettingTenantById, updatingTenant, showErrors } = tenantState;

  const [ccDraft, setCcDraft] = useState("");

  const updateForm = (patch: Partial<ITenantFormState>) => dispatch(setTenantForm(patch));

  const addCcEmail = () => {
    const trimmed = ccDraft.trim();
    if (!trimmed || !isValidEmail(trimmed)) return;
    if (form.ccEmails.includes(trimmed)) {
      setCcDraft("");
      return;
    }
    updateForm({ ccEmails: [...form.ccEmails, trimmed] });
    setCcDraft("");
  };

  const removeCcEmail = (email: string) => {
    updateForm({ ccEmails: form.ccEmails.filter((e) => e !== email) });
  };

  const onCcKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addCcEmail();
    }
  };

  useEffect(() => {
    if (tenantId) dispatch(getTenantById(tenantId));
    return () => {
      dispatch(resetTenantForm());
    };
  }, [dispatch, tenantId]);

  const errors = useMemo(
    () => ({
      firstName: form.firstName.trim() ? undefined : "Required",
      lastName: form.lastName.trim() ? undefined : "Required",
      email: isValidEmail(form.email) ? undefined : "Valid email required",
    }),
    [form],
  );

  const hasErrors = Object.values(errors).some(Boolean);
  const visibleErrors = showErrors ? errors : undefined;

  const handleUpdate = () => {
    if (!tenantId) return;
    if (hasErrors) {
      dispatch(setTenantShowErrors(true));
      return;
    }
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      ccEmails: form.ccEmails,
      billingName: form.billingName.trim(),
      billingAddress: form.billingAddress.trim(),
      licenceFees: form.licenceFees === "" ? 0 : Number(form.licenceFees),
      referralFees: form.referralFees === "" ? 0 : Number(form.referralFees),
    };
    dispatch(updateTenant({ tenantId, payload })).then((res: IDispatchResponse) => {
      if (res?.error) {
        dispatch(
          openSnackbar({
            message: res?.payload?.status_message || "Failed to update tenant",
            intent: "danger",
          }),
        );
      } else {
        dispatch(
          openSnackbar({
            message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.tenantUpdated,
            intent: "success",
          }),
        );
        navigate(ROUTES.ADMIN.TENANTS);
      }
    });
  };

  const orgName = currentTenant?.clientConnection?.name;

  return (
    <Stack direction="column" rowGap={4} sx={{ padding: 6, backgroundColor: "neutral.50", minHeight: "100vh" }}>
      {gettingTenantById ? (
        <Loader text="Loading tenant..." />
      ) : (
        <>
          <Typography level="h2" style={{ fontSize: "24px", fontWeight: 800 }}>
            Update Tenant
          </Typography>

          <SectionCard
            title="Tenant details"
            subtitle={orgName ? `Organisation: ${orgName}` : undefined}
            actions={
              currentTenant?.stripeCustomerId ? (
                <Stack direction="row" alignItems="center" columnGap={1}>
                  <Typography level="body-xs" textColor="neutral.500">
                    Stripe customer ID
                  </Typography>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "6px",
                      backgroundColor: "neutral.100",
                      border: "1px solid",
                      borderColor: "neutral.300",
                    }}
                  >
                    <Typography level="body-xs" style={{ fontFamily: "monospace", fontWeight: 600 }}>
                      {currentTenant.stripeCustomerId}
                    </Typography>
                  </Box>
                </Stack>
              ) : undefined
            }
          >
            <Stack direction="column" rowGap={3}>
              <Stack direction="row" columnGap={3}>
                <Box sx={{ flex: 1 }}>
                  <FormControl error={!!visibleErrors?.firstName} required>
                    <FormLabel>First name</FormLabel>
                    <Input
                      placeholder="e.g. John"
                      value={form.firstName}
                      onChange={(e) => updateForm({ firstName: e.target.value })}
                    />
                    {visibleErrors?.firstName && (
                      <Typography level="body-xs" textColor="danger.500" style={{ marginTop: 4 }}>
                        {visibleErrors.firstName}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormControl error={!!visibleErrors?.lastName} required>
                    <FormLabel>Last name</FormLabel>
                    <Input
                      placeholder="e.g. Doe"
                      value={form.lastName}
                      onChange={(e) => updateForm({ lastName: e.target.value })}
                    />
                    {visibleErrors?.lastName && (
                      <Typography level="body-xs" textColor="danger.500" style={{ marginTop: 4 }}>
                        {visibleErrors.lastName}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
              </Stack>

              <FormControl>
                <FormLabel>Billing name</FormLabel>
                <Input
                  placeholder="e.g. Acme Pty Ltd"
                  value={form.billingName}
                  onChange={(e) => updateForm({ billingName: e.target.value })}
                />
              </FormControl>

              <Stack direction="row" columnGap={3}>
                <Box sx={{ flex: 1 }}>
                  <FormControl error={!!visibleErrors?.email} required>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      placeholder="ap@company.com"
                      value={form.email}
                      onChange={(e) => updateForm({ email: e.target.value })}
                    />
                    {visibleErrors?.email && (
                      <Typography level="body-xs" textColor="danger.500" style={{ marginTop: 4 }}>
                        {visibleErrors.email}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormControl>
                    <FormLabel>Address</FormLabel>
                    <Input
                      placeholder="123 Example St, City"
                      value={form.address}
                      onChange={(e) => updateForm({ address: e.target.value })}
                    />
                  </FormControl>
                </Box>
              </Stack>

              <FormControl>
                <Stack direction="row" alignItems="center" justifyContent="space-between" columnGap={2}>
                  <FormLabel>Billing address</FormLabel>
                  <Checkbox
                    size="sm"
                    label="Same as address"
                    checked={!!form.billingAddress && form.billingAddress === form.address}
                    disabled={!form.address.trim() || !!form.billingAddress.trim()}
                    onChange={(e) => {
                      if (e.target.checked) updateForm({ billingAddress: form.address });
                    }}
                  />
                </Stack>
                <Input
                  placeholder="123 Example St, City"
                  value={form.billingAddress}
                  onChange={(e) => updateForm({ billingAddress: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>CC emails</FormLabel>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                    border: "1px solid",
                    borderColor: "neutral.300",
                    borderRadius: "6px",
                    padding: "6px 8px",
                    minHeight: 40,
                  }}
                >
                  {form.ccEmails.map((email) => (
                    <Pill key={email} accent={200}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        {email}
                        <IconButton size="sm" onClick={() => removeCcEmail(email)} aria-label={`Remove ${email}`}>
                          <CloseIcon style={{ fontSize: "0.85rem" }} />
                        </IconButton>
                      </span>
                    </Pill>
                  ))}
                  <Input
                    placeholder="another@company.com"
                    value={ccDraft}
                    onChange={(e) => setCcDraft(e.target.value)}
                    onKeyDown={onCcKeyDown}
                    onBlur={addCcEmail}
                    style={{ flex: 1, minWidth: 180, border: "none", boxShadow: "none" }}
                  />
                </Box>
                <Typography level="body-xs" textColor="neutral.500" style={{ marginTop: 4 }}>
                  Type and press Enter to add.
                </Typography>
              </FormControl>

              <Stack direction="row" columnGap={3}>
                <Box sx={{ flex: 1 }}>
                  <FormControl>
                    <FormLabel>Licence fees</FormLabel>
                    <Input
                      type="number"
                      placeholder="0"
                      startDecorator="$"
                      value={form.licenceFees}
                      onChange={(e) => updateForm({ licenceFees: e.target.value })}
                    />
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormControl>
                    <FormLabel>Referral fees</FormLabel>
                    <Input
                      type="number"
                      placeholder="0"
                      startDecorator="$"
                      value={form.referralFees}
                      onChange={(e) => updateForm({ referralFees: e.target.value })}
                    />
                  </FormControl>
                </Box>
              </Stack>
            </Stack>
          </SectionCard>

          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="primary"
              startDecorator={<SaveIcon />}
              disabled={hasErrors || updatingTenant}
              onClick={handleUpdate}
            >
              Update
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default TenantDetail;
